// // src/runtime/MyRuntimeProvider.tsx
"use client";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { AssistantRuntimeProvider, ChatModelAdapter, useLocalRuntime } from "@assistant-ui/react";
import { getToken } from "@/utils/localstorage";
import axios from "axios";
import { useAlphaAgentStore } from "@/state/alphaAgentStore";
import { DialogMessageType } from "@/types/commands";
import { logger } from "@/helpers/tslog";
//
const apiUrl = process.env.API_URL;

// Function to handle link click - close current popup and open alpha create popup
let isHandlingClick = false;
const handleLinkClick = () => {
  if (isHandlingClick) {
    return;
  }
  try {
    isHandlingClick = true;
    // Send message to open alpha create placeholder
    Office.context.ui.messageParent(DialogMessageType.OPEN_PLACEHOLDER_ALPHA_CREATE);
    // Reset the flag after a short delay
    setTimeout(() => {
      isHandlingClick = false;
    }, 1000);
  } catch (error) {
    logger.error("Error in handleLinkClick:", error);
    isHandlingClick = false;
  }
};

export const MyModelAdapter: ChatModelAdapter = {
  async *run({ messages }): AsyncGenerator {
    const authToken = await getToken();

    const lastMessage = messages[messages.length - 1];
    let userInput = "";

    if (lastMessage?.content && Array.isArray(lastMessage.content)) {
      const textPart = lastMessage.content.find(
        (part) => typeof part === "object" && "text" in part && typeof part.text === "string"
      );
      if (textPart) userInput = (textPart as any).text;
    }

    const isInitialization = userInput === "__INIT_ALPHA_EDIT__";

    let result;
    // Signal run start before any network requests
    try {
      window.dispatchEvent(new CustomEvent("ai-run-start"));
    } catch (e) {}
    if (isInitialization) {
      // Get the document data for initialization
      const documentId = localStorage.getItem("documentId");
      const response = await axios.get(`${apiUrl}/chat-edit-by-document-id`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          "Content-Type": "application/json",
        },
        params: {
          id: documentId,
        },
      });

      const { updateAnswerByStep, setDocumentData } = useAlphaAgentStore.getState();
      setDocumentData(response.data.document.llmGlobalState, response.data.document.documentContent);
      updateAnswerByStep(0, 3);

      result = await axios.post(
        `${apiUrl}/alpha-edit/initialize`,
        {
          existingGlobalState: JSON.parse(response.data.document.llmGlobalState),
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
        }
      );
    } else {
      result = await axios.post(
        `${apiUrl}/alpha-edit/interact`,
        { userInput },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
    }

    localStorage.setItem("alphaCreateData", JSON.stringify(result.data.data.globalState));
    const { markAnswered, setDocumentData } = useAlphaAgentStore.getState();
    const step = 0;

    if (result.data.data.step === 2) {
      markAnswered(step);
      setDocumentData(result.data.data.globalState, "");
    }

    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    const fullText = result.data.data.response;
    for (let i = 1; i <= fullText.length; i++) {
      if (i === 1) {
        try {
          window.dispatchEvent(new CustomEvent("ai-first-token"));
        } catch (e) {}
      }
      yield {
        content: [{ type: "text", text: fullText.slice(0, i) }],
      };

      // Check for the link during streaming and add click handler as soon as it appears
      if (i % 50 === 0 || i === fullText.length) {
        // Check every 50 characters or at the end
        setTimeout(() => {
          try {
            const link = document.getElementById("goto-alpha-create") as HTMLAnchorElement;
            if (link && !link.onclick) {
              // Only add handler if not already added
              link.href = "";
              link.onclick = (event) => {
                event.preventDefault();
                if ((window as any).handleLinkClick) {
                  (window as any).handleLinkClick();
                }
                return false;
              };
            }
          } catch (e) {
            // ignore
          }
        }, 10);
      }

      await delay(8);
    }
    try {
      window.dispatchEvent(new CustomEvent("ai-run-end"));
    } catch (e) {}
  },
};

export function MyRuntimeProvider({ children }: { children: ReactNode }) {
  return <MyRuntimeProviderReady>{children}</MyRuntimeProviderReady>;
}

function MyRuntimeProviderReady({ children }: { children: ReactNode }) {
  const runtime = useLocalRuntime(MyModelAdapter, {
    initialMessages: [],
  });

  // Make handleLinkClick available globally
  useEffect(() => {
    (window as any).handleLinkClick = handleLinkClick;

    // Cleanup function to remove the global function when component unmounts
    return () => {
      delete (window as any).handleLinkClick;
    };
  }, []);

  // Trigger initialize after mount to show the initial message with streaming
  const initSentRef = useRef(false);
  useEffect(() => {
    if (initSentRef.current) return;

    try {
      const thread = (runtime as any)?.thread;
      if (thread && typeof thread.append === "function") {
        thread.append({
          role: "system",
          content: [{ type: "text", text: "__INIT_ALPHA_EDIT__" }],
          startRun: true,
        });
        initSentRef.current = true;
      }
    } catch (e) {
      // ignore
    }
  }, [runtime]);

  return <AssistantRuntimeProvider runtime={runtime}>{children}</AssistantRuntimeProvider>;
}
